/**
 * Team Share links — shared team bookmarks in user-managed categories,
 * shown ABOVE the keychain in the Team Share dialog. A realtime search
 * bar filters across title, description, URL, category name and the
 * (hidden) keywords. Any internal user can add categories and links.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Search, Plus, ExternalLink, Pencil, Trash2, FolderPlus,
  ChevronDown, ChevronRight, Link2,
} from "lucide-react";

interface TeamLinkItem {
  id: number;
  categoryId: number;
  title: string;
  url: string;
  description: string | null;
  keywords: string | null;
  createdAt: string;
  createdByName: string;
}

interface LinkCategoryItem {
  id: number;
  name: string;
  links: TeamLinkItem[];
}

const EMPTY_FORM = { title: "", url: "", description: "", keywords: "", categoryId: "" };

export function TeamLinksSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [catName, setCatName] = useState("");
  const [linkDialog, setLinkDialog] = useState<null | { editId: number | null }>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<null | { kind: "link" | "category"; id: number; name: string }>(null);

  const { data, isLoading } = useQuery<{ categories: LinkCategoryItem[] }>({
    queryKey: ["/api/team-links"],
  });
  const categories = data?.categories ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/team-links"] });

  const fail = (e: unknown) =>
    toast({ title: "Fehler", description: e instanceof Error ? e.message : "Aktion fehlgeschlagen", variant: "destructive" });

  const createCategory = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/team-links/categories", { name });
      if (!res.ok) throw new Error((await res.json()).error);
      return res.json();
    },
    onSuccess: () => { invalidate(); setShowCatDialog(false); setCatName(""); },
    onError: fail,
  });

  const saveLink = useMutation({
    mutationFn: async () => {
      const payload = {
        categoryId: Number(form.categoryId),
        title: form.title.trim(),
        url: form.url.trim(),
        description: form.description.trim() || null,
        keywords: form.keywords.trim() || null,
      };
      const res = linkDialog?.editId
        ? await apiRequest("PATCH", `/api/team-links/links/${linkDialog.editId}`, payload)
        : await apiRequest("POST", "/api/team-links/links", payload);
      if (!res.ok) throw new Error((await res.json()).error);
      return res.json();
    },
    onSuccess: () => { invalidate(); setLinkDialog(null); setForm(EMPTY_FORM); },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: async (t: { kind: "link" | "category"; id: number }) => {
      const url = t.kind === "link" ? `/api/team-links/links/${t.id}` : `/api/team-links/categories/${t.id}`;
      const res = await apiRequest("DELETE", url);
      if (!res.ok) throw new Error((await res.json()).error);
    },
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
    onError: fail,
  });

  // Distinct creators for the user filter dropdown.
  const creators = useMemo(() => {
    const names = new Set<string>();
    for (const c of categories) for (const l of c.links) names.add(l.createdByName);
    return Array.from(names).sort((a, b) => a.localeCompare(b, "de"));
  }, [categories]);

  // Realtime quick-match: filters links by title/description/url/keywords
  // and category name, plus an optional creator filter.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byUser = userFilter !== "all";
    if (!q && !byUser) return categories;
    return categories
      .map((c) => {
        const catMatch = q !== "" && c.name.toLowerCase().includes(q);
        const links = c.links.filter((l) => {
          if (byUser && l.createdByName !== userFilter) return false;
          if (!q) return true;
          return (
            catMatch ||
            l.title.toLowerCase().includes(q) ||
            (l.description || "").toLowerCase().includes(q) ||
            l.url.toLowerCase().includes(q) ||
            (l.keywords || "").toLowerCase().includes(q)
          );
        });
        return { ...c, links };
      })
      .filter((c) => c.links.length > 0 || (!byUser && q !== "" && c.name.toLowerCase().includes(q)));
  }, [categories, search, userFilter]);

  const openAddLink = (categoryId?: number) => {
    setForm({ ...EMPTY_FORM, categoryId: categoryId ? String(categoryId) : (categories[0] ? String(categories[0].id) : "") });
    setLinkDialog({ editId: null });
  };

  const openEditLink = (l: TeamLinkItem) => {
    setForm({
      title: l.title, url: l.url, description: l.description || "",
      keywords: l.keywords || "", categoryId: String(l.categoryId),
    });
    setLinkDialog({ editId: l.id });
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString("de-DE");

  return (
    <div className="space-y-3">
      {/* Search + actions */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Links durchsuchen …"
            className="pl-8"
            data-testid="input-links-search"
          />
        </div>
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="select-links-user-filter">
            <SelectValue placeholder="Alle Nutzer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Nutzer</SelectItem>
            {creators.map((name) => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setShowCatDialog(true)} data-testid="button-add-link-category">
          <FolderPlus className="h-4 w-4 mr-1.5" /> Kategorie
        </Button>
        <Button size="sm" onClick={() => openAddLink()} disabled={categories.length === 0} data-testid="button-add-link">
          <Plus className="h-4 w-4 mr-1.5" /> Link
        </Button>
      </div>

      {isLoading && <p className="text-sm text-gray-500 px-1">Wird geladen …</p>}
      {!isLoading && categories.length === 0 && (
        <p className="text-sm text-gray-500 px-1">
          Noch keine Link-Kategorien — lege mit „Kategorie" die erste an.
        </p>
      )}
      {!isLoading && categories.length > 0 && filtered.length === 0 && (
        <p className="text-sm text-gray-500 px-1">Keine Treffer für „{search}".</p>
      )}

      {filtered.map((cat) => (
        <div key={cat.id} className="rounded-lg border border-gray-700/60 bg-gray-800/30">
          <div className="flex items-center gap-2 px-3 py-2">
            <button
              className="flex items-center gap-2 flex-1 text-left min-w-0"
              onClick={() => setCollapsed((p) => ({ ...p, [cat.id]: !p[cat.id] }))}
              data-testid={`category-toggle-${cat.id}`}
            >
              {collapsed[cat.id] ? <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
              <span className="text-sm font-medium text-gray-200 truncate">{cat.name}</span>
              <span className="text-xs text-gray-500">{cat.links.length}</span>
            </button>
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openAddLink(cat.id)} title="Link hinzufügen">
              <Plus className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost" size="sm" className="h-7 px-2 text-gray-500 hover:text-red-400"
              onClick={() => setDeleteTarget({ kind: "category", id: cat.id, name: cat.name })}
              title="Kategorie löschen"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>

          {!collapsed[cat.id] && cat.links.length > 0 && (
            <div className="border-t border-gray-700/50 divide-y divide-gray-700/40">
              {cat.links.map((l) => (
                <div key={l.id} className="flex items-start gap-3 px-3 py-2.5 group" data-testid={`link-row-${l.id}`}>
                  <Link2 className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-gray-100 hover:text-sky-300 inline-flex items-center gap-1.5 break-all"
                    >
                      {l.title}
                      <ExternalLink className="h-3 w-3 opacity-60 shrink-0" />
                    </a>
                    {l.description && <p className="text-xs text-gray-400 mt-0.5">{l.description}</p>}
                    <p className="text-[11px] text-gray-500 mt-1">
                      {l.createdByName} · {formatDate(l.createdAt)}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openEditLink(l)} title="Bearbeiten">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="h-7 px-2 text-gray-500 hover:text-red-400"
                      onClick={() => setDeleteTarget({ kind: "link", id: l.id, name: l.title })}
                      title="Löschen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* New category */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Neue Link-Kategorie</DialogTitle>
            <DialogDescription>Kategorien ordnen die geteilten Links des Teams.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cat-name">Name</Label>
            <Input id="cat-name" value={catName} onChange={(e) => setCatName(e.target.value)} maxLength={100} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatDialog(false)}>Abbrechen</Button>
            <Button onClick={() => createCategory.mutate(catName.trim())} disabled={!catName.trim() || createCategory.isPending}>
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit link */}
      <Dialog open={linkDialog !== null} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{linkDialog?.editId ? "Link bearbeiten" : "Neuer Link"}</DialogTitle>
            <DialogDescription>Schlagwörter werden nur für die Suche genutzt und nicht angezeigt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Kategorie</Label>
              <Select value={form.categoryId} onValueChange={(v) => setForm((f) => ({ ...f, categoryId: v }))}>
                <SelectTrigger><SelectValue placeholder="Kategorie wählen" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Titel</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={255} />
            </div>
            <div className="space-y-1.5">
              <Label>Link (URL)</Label>
              <Input value={form.url} placeholder="https://…" onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Kurzbeschreibung</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} maxLength={500} />
            </div>
            <div className="space-y-1.5">
              <Label>Schlagwörter (nur für die Suche)</Label>
              <Input
                value={form.keywords}
                placeholder="z. B. rechnung, vorlage, briefing"
                onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(null)}>Abbrechen</Button>
            <Button
              onClick={() => saveLink.mutate()}
              disabled={!form.title.trim() || !form.url.trim() || !form.categoryId || saveLink.isPending}
            >
              {linkDialog?.editId ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.kind === "category" ? "Kategorie löschen?" : "Link löschen?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "category"
                ? `„${deleteTarget?.name}" und alle darin enthaltenen Links werden dauerhaft gelöscht.`
                : `„${deleteTarget?.name}" wird dauerhaft gelöscht.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && remove.mutate(deleteTarget)}
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
